import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type {
  ToolName,
  ToolState,
  ToolsState,
  LayerSettingsToolState,
  GroupSettingsToolState,
  MapViewSettingsToolState,
  NotesToolState,
  DrawToolState,
  DrawTargetLayer,
} from '@/containers/Tools/types'
import type { Layer } from '@/containers/Toc/context/types'
import {
  setToolVisible,
  setToolCollapsed,
  setToolPosition,
  setLayerSettingsLayer,
  closeLayerSettings,
  setGroupSettingsGroup,
  closeGroupSettings,
  setMapViewSettingsGroup,
  setDrawTargetLayer,
} from '@/actions/tools'
import { openNotes } from '@/actions'

// =============================================================================
// Architecture Notes: Redux Migration Path
// =============================================================================
//
// CURRENT ARCHITECTURE:
// This Context acts as an abstraction layer over Redux for Tools state management.
// Components consume tools via Context hooks (useLayerSettings, useGroupSettings, etc.)
// rather than directly accessing Redux, which provides:
//
// 1. Clean API: Tool-specific hooks with focused interfaces
// 2. Decoupling: Components don't depend on Redux directly
// 3. Future-proof: Easy migration path if moving away from Redux
//
// MIGRATION PATH (if moving away from Redux):
// To migrate to pure React Context + useReducer:
//
// 1. Replace Redux state access:
//    - Remove: `useSelector((state: any) => state.toolConfig)`
//    - Add: `const [toolsState, dispatch] = useReducer(toolsReducer, initialState)`
//
// 2. Replace Redux actions:
//    - Remove: Redux action imports and `useDispatch()`
//    - Add: Local reducer actions that match the same action types
//    - Update: All `dispatch(actionCreator())` calls to `dispatch({ type, payload })`
//
// 3. Add persistence (if needed):
//    - Use `useEffect` to sync state to localStorage
//    - Use `useEffect` to restore state from localStorage on mount
//
// 4. Update reducer:
//    - Move logic from `src/reducers/reducer_tools.js` to a local `toolsReducer`
//    - Keep the same action types and payload structure
//
// 5. No component changes needed:
//    - All consuming components will continue to work unchanged
//    - The Context API (hooks, methods) remains the same
//
// BENEFITS OF CURRENT APPROACH:
// - Redux DevTools for debugging
// - Built-in persistence options (redux-persist)
// - State accessible outside React tree if needed
// - Clear migration path when ready
//
// =============================================================================
// Context Types
// =============================================================================

export interface MapView {
  id: string | number
  [key: string]: unknown
}

export interface MapViewGroup {
  id: string | number
  [key: string]: unknown
}

interface ToolsReduxState {
  toolConfig: ToolsState | null
  mapViewNotes: MapView | null
}

/** Action the LayerSettings unsaved-changes guard is asked to approve */
export type LayerSettingsGuardAction =
  | { type: 'switch'; layer: Layer }
  | { type: 'close' }

/**
 * Guard consulted before LayerSettings switches layer or closes.
 * Return `true` to intercept (block) the action.
 */
export type LayerSettingsGuard = (action: LayerSettingsGuardAction) => boolean

interface ToolsContextValue {
  // ---------------------------------------------------------------------------
  // State (read-only for consumers)
  // ---------------------------------------------------------------------------

  /** Get tool state by name */
  getTool: (toolName: ToolName) => ToolState | undefined

  /** Get all visible tool names */
  visibleTools: ToolName[]

  /** Check if a tool is visible */
  isToolVisible: (toolName: ToolName) => boolean

  // ---------------------------------------------------------------------------
  // Generic Tool Operations
  // ---------------------------------------------------------------------------

  /** Set tool visibility */
  setToolVisible: (toolName: ToolName, visible: boolean) => void

  /** Set tool collapsed state */
  setToolCollapsed: (toolName: ToolName, collapsed: boolean) => void

  /** Set tool position */
  setToolPosition: (
    toolName: ToolName,
    positionX: number | null,
    positionY: number | null
  ) => void

  // ---------------------------------------------------------------------------
  // Stable Callbacks (for convenience hooks to create memoized functions)
  // ---------------------------------------------------------------------------

  /**
   * Open LayerSettings for a layer (consults the unsaved-changes guard).
   * Returns `false` when the guard intercepted the action.
   */
  handleLayerSettingsOpen: (layer: Layer) => boolean

  /**
   * Close LayerSettings (consults the unsaved-changes guard).
   * Returns `false` when the guard intercepted the action.
   */
  handleLayerSettingsClose: () => boolean

  /** Open LayerSettings for a layer, bypassing the unsaved-changes guard */
  forceLayerSettingsOpen: (layer: Layer) => void

  /** Close LayerSettings, bypassing the unsaved-changes guard */
  forceLayerSettingsClose: () => void

  /**
   * Register a guard consulted before LayerSettings is switched to another
   * layer or closed from outside the panel. Return `true` from the guard to
   * intercept (block) the action — the guard owns showing any confirmation UI
   * and re-driving the action via the `force*` variants. Returns an
   * unregister function.
   */
  registerLayerSettingsGuard: (guard: LayerSettingsGuard) => () => void

  /** Open GroupSettings for a group */
  handleGroupSettingsOpen: (group: Layer) => void

  /** Close GroupSettings */
  handleGroupSettingsClose: () => void

  /** Set Draw target layer */
  handleDrawSetTargetLayer: (targetLayer: DrawTargetLayer | null) => void

  // ---------------------------------------------------------------------------
  // LayerSettings Hook
  // ---------------------------------------------------------------------------

  /** Hook for LayerSettings tool */
  useLayerSettings: () => {
    isVisible: boolean
    layer: Layer | null
    open: (layer: Layer) => void
    close: () => void
    toggle: (layer: Layer) => void
    toggleCollapse: () => void
    collapsed: boolean
  }

  // ---------------------------------------------------------------------------
  // GroupSettings Hook
  // ---------------------------------------------------------------------------

  /** Hook for GroupSettings tool */
  useGroupSettings: () => {
    isVisible: boolean
    group: Layer | null
    open: (group: Layer) => void
    close: () => void
    toggle: (group: Layer) => void
    toggleCollapse: () => void
    collapsed: boolean
  }

  // ---------------------------------------------------------------------------
  // Legend Hook
  // ---------------------------------------------------------------------------

  /** Hook for Legend tool */
  useLegend: () => {
    isVisible: boolean
    collapsed: boolean
    toggleCollapse: () => void
    positionX: number | null
    positionY: number | null
    setPosition: (x: number | null, y: number | null) => void
  }

  // ---------------------------------------------------------------------------
  // MapViewSettings Hook
  // ---------------------------------------------------------------------------

  /** Open MapViewSettings for a map view / group */
  handleMapViewSettingsOpen: (mapViewGroup: MapViewGroup | null) => void

  /** Close MapViewSettings */
  handleMapViewSettingsClose: () => void

  /** Hook for MapViewSettings tool */
  useMapViewSettings: () => {
    isVisible: boolean
    mapViewGroup: MapViewGroup | null
    collapsed: boolean
    open: (mapViewGroup: MapViewGroup | null) => void
    close: () => void
    toggle: (mapViewGroup: MapViewGroup | null) => void
    toggleCollapse: () => void
  }

  // ---------------------------------------------------------------------------
  // Notes Hook
  // ---------------------------------------------------------------------------

  /** Show Notes panel for a map view */
  handleNotesShow: (mapView: MapView | null) => void

  /** Hide Notes panel */
  handleNotesHide: () => void

  /** Hook for Notes tool */
  useNotes: () => {
    isVisible: boolean
    collapsed: boolean
    mapViewNotes: MapView | null
    show: (mapView: MapView | null) => void
    hide: () => void
    toggleCollapse: () => void
  }

  // ---------------------------------------------------------------------------
  // Draw Hook
  // ---------------------------------------------------------------------------

  /** Hook for Draw tool */
  useDraw: () => {
    isVisible: boolean
    toggle: () => void
    targetLayer: DrawTargetLayer | null
    setTargetLayer: (targetLayer: DrawTargetLayer | null) => void
  }
}

interface ToolsProviderProps {
  children: ReactNode
}

// =============================================================================
// Context Creation
// =============================================================================

const ToolsContext = createContext<ToolsContextValue | undefined>(undefined)

// =============================================================================
// Provider Component
// =============================================================================

export const ToolsProvider: React.FC<ToolsProviderProps> = ({ children }) => {
  const dispatch = useDispatch()

  // Get tools state from Redux (normalized format only)
  const toolsState = useSelector((state: ToolsReduxState) => state.toolConfig)

  // Notes data lives in its own Redux slice (mapViewNotes)
  const mapViewNotes = useSelector(
    (state: ToolsReduxState) => state.mapViewNotes
  )

  // ---------------------------------------------------------------------------
  // Get normalized state
  // ---------------------------------------------------------------------------

  const normalizedState = useMemo((): ToolsState['tools'] | null => {
    if (
      !toolsState ||
      typeof toolsState !== 'object' ||
      !('tools' in toolsState)
    ) {
      return null
    }
    return toolsState.tools
  }, [toolsState])

  // ---------------------------------------------------------------------------
  // State Getters
  // ---------------------------------------------------------------------------

  const getTool = useCallback(
    (toolName: ToolName): ToolState | undefined => {
      return normalizedState?.[toolName]
    },
    [normalizedState]
  )

  const visibleTools = useMemo((): ToolName[] => {
    if (!normalizedState) return []
    return (Object.keys(normalizedState) as ToolName[]).filter(
      toolName => normalizedState[toolName]?.visible
    )
  }, [normalizedState])

  const isToolVisible = useCallback(
    (toolName: ToolName): boolean => {
      return normalizedState?.[toolName]?.visible ?? false
    },
    [normalizedState]
  )

  // ---------------------------------------------------------------------------
  // Generic Tool Operations
  // ---------------------------------------------------------------------------

  const handleSetToolVisible = useCallback(
    (toolName: ToolName, visible: boolean) => {
      dispatch(setToolVisible(toolName, visible))
    },
    [dispatch]
  )

  const handleSetToolCollapsed = useCallback(
    (toolName: ToolName, collapsed: boolean) => {
      dispatch(setToolCollapsed(toolName, collapsed))
    },
    [dispatch]
  )

  const handleSetToolPosition = useCallback(
    (
      toolName: ToolName,
      positionX: number | null,
      positionY: number | null
    ) => {
      dispatch(setToolPosition(toolName, positionX, positionY))
    },
    [dispatch]
  )

  // ---------------------------------------------------------------------------
  // LayerSettings Hook Implementation
  // ---------------------------------------------------------------------------

  // Guard registered by the LayerSettings panel while it is mounted. Lets the
  // panel intercept layer switches / closes triggered from outside it (e.g.
  // TOC cog buttons) when a form inside has unsaved changes. (MAM-756)
  const layerSettingsGuardRef = useRef<LayerSettingsGuard | null>(null)

  const registerLayerSettingsGuard = useCallback(
    (guard: LayerSettingsGuard) => {
      layerSettingsGuardRef.current = guard
      return () => {
        if (layerSettingsGuardRef.current === guard) {
          layerSettingsGuardRef.current = null
        }
      }
    },
    []
  )

  const forceLayerSettingsOpen = useCallback(
    (newLayer: Layer) => {
      dispatch(setLayerSettingsLayer(newLayer))
    },
    [dispatch]
  )

  const forceLayerSettingsClose = useCallback(() => {
    dispatch(closeLayerSettings())
  }, [dispatch])

  const handleLayerSettingsOpen = useCallback(
    (newLayer: Layer) => {
      if (
        layerSettingsGuardRef.current?.({ type: 'switch', layer: newLayer })
      ) {
        return false
      }
      forceLayerSettingsOpen(newLayer)
      return true
    },
    [forceLayerSettingsOpen]
  )

  const handleLayerSettingsClose = useCallback(() => {
    if (layerSettingsGuardRef.current?.({ type: 'close' })) return false
    forceLayerSettingsClose()
    return true
  }, [forceLayerSettingsClose])

  const useLayerSettingsHook = useCallback(() => {
    const tool = getTool('LayerSettings') as LayerSettingsToolState | undefined
    const isVisible = tool?.visible ?? false
    const layer = tool?.layer ?? null
    const collapsed = tool?.collapsed ?? false

    // Create stable function references that don't change unless dependencies change
    // These functions are recreated but the returned object is memoized in the convenience hook
    const open = (newLayer: Layer) => {
      handleLayerSettingsOpen(newLayer)
    }

    const close = () => {
      handleLayerSettingsClose()
    }

    const toggle = (newLayer: Layer) => {
      if (isVisible && layer?.toc?.id === newLayer.toc?.id) {
        close()
      } else {
        open(newLayer)
      }
    }

    const toggleCollapse = () => {
      handleSetToolCollapsed('LayerSettings', !collapsed)
    }

    return {
      isVisible,
      layer,
      open,
      close,
      toggle,
      toggleCollapse,
      collapsed,
    }
  }, [
    getTool,
    handleLayerSettingsOpen,
    handleLayerSettingsClose,
    handleSetToolCollapsed,
  ])

  // ---------------------------------------------------------------------------
  // GroupSettings Hook Implementation
  // ---------------------------------------------------------------------------

  // Create callbacks at provider level to avoid nested useCallback
  const handleGroupSettingsOpen = useCallback(
    (newGroup: Layer) => {
      dispatch(setGroupSettingsGroup(newGroup))
    },
    [dispatch]
  )

  const handleGroupSettingsClose = useCallback(() => {
    dispatch(closeGroupSettings())
  }, [dispatch])

  const useGroupSettingsHook = useCallback(() => {
    const tool = getTool('GroupSettings') as GroupSettingsToolState | undefined
    const isVisible = tool?.visible ?? false
    const group = tool?.group ?? null
    const collapsed = tool?.collapsed ?? false

    // Create plain functions that reference provider-level callbacks and current state
    const open = (newGroup: Layer) => {
      handleGroupSettingsOpen(newGroup)
    }

    const close = () => {
      handleGroupSettingsClose()
    }

    const toggle = (newGroup: Layer) => {
      if (isVisible && group?.groupId === newGroup.groupId) {
        close()
      } else {
        open(newGroup)
      }
    }

    const toggleCollapse = () => {
      handleSetToolCollapsed('GroupSettings', !collapsed)
    }

    return {
      isVisible,
      group,
      open,
      close,
      toggle,
      toggleCollapse,
      collapsed,
    }
  }, [
    getTool,
    handleGroupSettingsOpen,
    handleGroupSettingsClose,
    handleSetToolCollapsed,
  ])

  // ---------------------------------------------------------------------------
  // Legend Hook Implementation
  // ---------------------------------------------------------------------------

  const useLegendHook = useCallback(() => {
    const tool = getTool('Legend')
    const isVisible = tool?.visible ?? false
    const collapsed = tool && 'collapsed' in tool ? tool.collapsed : false
    const positionX = tool && 'positionX' in tool ? tool.positionX : null
    const positionY = tool && 'positionY' in tool ? tool.positionY : null

    // Create plain functions that reference provider-level callbacks and current state
    const toggleCollapse = () => {
      handleSetToolCollapsed('Legend', !collapsed)
    }

    const setPosition = (x: number | null, y: number | null) => {
      handleSetToolPosition('Legend', x, y)
    }

    return {
      isVisible,
      collapsed,
      toggleCollapse,
      positionX,
      positionY,
      setPosition,
    }
  }, [getTool, handleSetToolCollapsed, handleSetToolPosition])

  // ---------------------------------------------------------------------------
  // MapViewSettings Hook Implementation
  // ---------------------------------------------------------------------------

  const handleMapViewSettingsOpen = useCallback(
    (mapViewGroup: MapViewGroup | null) => {
      dispatch(setMapViewSettingsGroup(mapViewGroup))
    },
    [dispatch]
  )

  const handleMapViewSettingsClose = useCallback(() => {
    dispatch(setMapViewSettingsGroup(null))
  }, [dispatch])

  const useMapViewSettingsHook = useCallback(() => {
    const tool = getTool('MapViewSettings') as
      | MapViewSettingsToolState
      | undefined
    const isVisible = tool?.visible ?? false
    const mapViewGroup = (tool?.mapViewGroup ?? null) as MapViewGroup | null
    const collapsed = tool?.collapsed ?? false

    const open = (newMapViewGroup: MapViewGroup | null) => {
      if (!newMapViewGroup) return
      handleMapViewSettingsOpen(newMapViewGroup)
    }

    const close = () => {
      handleMapViewSettingsClose()
    }

    const toggle = (newMapViewGroup: MapViewGroup | null) => {
      if (!newMapViewGroup) return

      if (isVisible && mapViewGroup?.id === newMapViewGroup.id) {
        close()
      } else {
        open(newMapViewGroup)
      }
    }

    const toggleCollapse = () => {
      handleSetToolCollapsed('MapViewSettings', !collapsed)
    }

    return {
      isVisible,
      mapViewGroup,
      collapsed,
      open,
      close,
      toggle,
      toggleCollapse,
    }
  }, [
    getTool,
    handleMapViewSettingsOpen,
    handleMapViewSettingsClose,
    handleSetToolCollapsed,
  ])

  // ---------------------------------------------------------------------------
  // Notes Hook Implementation
  // ---------------------------------------------------------------------------

  // Keep openNotes dispatch first so reducers that depend on mapView payload
  // have it available before Notes visibility flips to true/false.
  const handleNotesShow = useCallback(
    (mapView: MapView | null) => {
      dispatch(openNotes(mapView))
      dispatch(setToolVisible('Notes', true))
    },
    [dispatch]
  )

  const handleNotesHide = useCallback(() => {
    dispatch(openNotes(null))
    dispatch(setToolVisible('Notes', false))
  }, [dispatch])

  const useNotesHook = useCallback(() => {
    const tool = getTool('Notes') as NotesToolState | undefined
    const isVisible = tool?.visible ?? false
    const collapsed = tool?.collapsed ?? false

    const show = (mapView: MapView | null) => {
      handleNotesShow(mapView)
    }

    const hide = () => {
      handleNotesHide()
    }

    const toggleCollapse = () => {
      handleSetToolCollapsed('Notes', !collapsed)
    }

    return {
      isVisible,
      collapsed,
      mapViewNotes,
      show,
      hide,
      toggleCollapse,
    }
  }, [
    getTool,
    handleNotesShow,
    handleNotesHide,
    handleSetToolCollapsed,
    mapViewNotes,
  ])

  // ---------------------------------------------------------------------------
  // Draw Hook Implementation
  // ---------------------------------------------------------------------------

  // Create callback at provider level to avoid nested useCallback
  const handleDrawSetTargetLayer = useCallback(
    (newTargetLayer: DrawTargetLayer | null) => {
      dispatch(setDrawTargetLayer(newTargetLayer))
    },
    [dispatch]
  )

  const useDrawHook = useCallback(() => {
    const tool = getTool('Draw') as DrawToolState | undefined
    const isVisible = tool?.visible ?? false
    const targetLayer = tool?.targetLayer ?? null

    // Create plain functions that reference provider-level callbacks and current state
    const toggle = () => {
      handleSetToolVisible('Draw', !isVisible)
    }

    const setTargetLayer = (newTargetLayer: DrawTargetLayer | null) => {
      handleDrawSetTargetLayer(newTargetLayer)
    }

    return {
      isVisible,
      toggle,
      targetLayer,
      setTargetLayer,
    }
  }, [getTool, handleSetToolVisible, handleDrawSetTargetLayer])

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value = useMemo<ToolsContextValue>(
    () => ({
      getTool,
      visibleTools,
      isToolVisible,
      setToolVisible: handleSetToolVisible,
      setToolCollapsed: handleSetToolCollapsed,
      setToolPosition: handleSetToolPosition,
      handleLayerSettingsOpen,
      handleLayerSettingsClose,
      forceLayerSettingsOpen,
      forceLayerSettingsClose,
      registerLayerSettingsGuard,
      handleGroupSettingsOpen,
      handleGroupSettingsClose,
      handleMapViewSettingsOpen,
      handleMapViewSettingsClose,
      handleNotesShow,
      handleNotesHide,
      handleDrawSetTargetLayer,
      useLayerSettings: useLayerSettingsHook,
      useGroupSettings: useGroupSettingsHook,
      useMapViewSettings: useMapViewSettingsHook,
      useNotes: useNotesHook,
      useLegend: useLegendHook,
      useDraw: useDrawHook,
    }),
    [
      getTool,
      visibleTools,
      isToolVisible,
      handleSetToolVisible,
      handleSetToolCollapsed,
      handleSetToolPosition,
      handleLayerSettingsOpen,
      handleLayerSettingsClose,
      forceLayerSettingsOpen,
      forceLayerSettingsClose,
      registerLayerSettingsGuard,
      handleGroupSettingsOpen,
      handleGroupSettingsClose,
      handleMapViewSettingsOpen,
      handleMapViewSettingsClose,
      handleNotesShow,
      handleNotesHide,
      handleDrawSetTargetLayer,
      useLayerSettingsHook,
      useGroupSettingsHook,
      useMapViewSettingsHook,
      useNotesHook,
      useLegendHook,
      useDrawHook,
    ]
  )

  return <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * useTools - Access the Tools context value.
 *
 * Provides access to all tool state and operations. Must be used within a ToolsProvider.
 *
 * @returns The ToolsContextValue with state, helpers, and hooks
 * @throws Error if used outside of ToolsProvider
 */
export const useTools = (): ToolsContextValue => {
  const context = useContext(ToolsContext)
  if (context === undefined) {
    throw new Error('useTools must be used within a ToolsProvider')
  }
  return context
}

// =============================================================================
// Convenience Hooks (exported for direct use)
// =============================================================================

/**
 * useLayerSettings - Hook for LayerSettings tool state and operations
 */
export const useLayerSettings = () => {
  const {
    useLayerSettings: hook,
    handleLayerSettingsOpen,
    handleLayerSettingsClose,
    forceLayerSettingsOpen,
    forceLayerSettingsClose,
    setToolCollapsed,
  } = useTools()
  const result = hook()

  // Memoize the returned object to prevent unnecessary re-renders
  // Use stable provider-level callbacks in dependencies, not the wrapper functions
  return useMemo(
    () => ({
      isVisible: result.isVisible,
      layer: result.layer,
      collapsed: result.collapsed,
      open: handleLayerSettingsOpen,
      close: handleLayerSettingsClose,
      forceOpen: forceLayerSettingsOpen,
      forceClose: forceLayerSettingsClose,
      toggle: (newLayer: Layer) => {
        if (result.isVisible && result.layer?.toc?.id === newLayer.toc?.id) {
          handleLayerSettingsClose()
        } else {
          handleLayerSettingsOpen(newLayer)
        }
      },
      toggleCollapse: () => {
        setToolCollapsed('LayerSettings', !result.collapsed)
      },
    }),
    [
      result.isVisible,
      result.layer?.toc?.id,
      result.collapsed,
      handleLayerSettingsOpen,
      handleLayerSettingsClose,
      forceLayerSettingsOpen,
      forceLayerSettingsClose,
      setToolCollapsed,
    ]
  )
}

/**
 * useLayerSettingsUnsavedGuard - Register a guard that intercepts LayerSettings
 * layer switches and closes triggered from outside the panel (e.g. TOC cog
 * buttons) while the consuming component is mounted.
 *
 * The guard receives the attempted action and returns `true` to block it; the
 * guard owner is then responsible for confirming with the user and re-driving
 * the action via `forceOpen`/`forceClose`. Pass a stable callback (wrap in
 * `useCallback`) to avoid re-registering on every render.
 */
export const useLayerSettingsUnsavedGuard = (guard: LayerSettingsGuard) => {
  const { registerLayerSettingsGuard } = useTools()

  useEffect(
    () => registerLayerSettingsGuard(guard),
    [guard, registerLayerSettingsGuard]
  )
}

/**
 * useGroupSettings - Hook for GroupSettings tool state and operations
 */
export const useGroupSettings = () => {
  const {
    useGroupSettings: hook,
    handleGroupSettingsOpen,
    handleGroupSettingsClose,
    setToolCollapsed,
  } = useTools()
  const result = hook()

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isVisible: result.isVisible,
      group: result.group,
      collapsed: result.collapsed,
      open: handleGroupSettingsOpen,
      close: handleGroupSettingsClose,
      toggle: (newGroup: Layer) => {
        if (result.isVisible && result.group?.groupId === newGroup.groupId) {
          handleGroupSettingsClose()
        } else {
          handleGroupSettingsOpen(newGroup)
        }
      },
      toggleCollapse: () => {
        setToolCollapsed('GroupSettings', !result.collapsed)
      },
    }),
    [
      result.isVisible,
      result.group?.groupId,
      result.collapsed,
      handleGroupSettingsOpen,
      handleGroupSettingsClose,
      setToolCollapsed,
    ]
  )
}

/**
 * useLegend - Hook for Legend tool state and operations
 */
export const useLegend = () => {
  const { useLegend: hook, setToolCollapsed, setToolPosition } = useTools()
  const result = hook()

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isVisible: result.isVisible,
      collapsed: result.collapsed,
      positionX: result.positionX,
      positionY: result.positionY,
      toggleCollapse: () => {
        setToolCollapsed('Legend', !result.collapsed)
      },
      setPosition: (x: number | null, y: number | null) => {
        setToolPosition('Legend', x, y)
      },
    }),
    [
      result.isVisible,
      result.collapsed,
      result.positionX,
      result.positionY,
      setToolCollapsed,
      setToolPosition,
    ]
  )
}

/**
 * useMapViewSettings - Hook for MapViewSettings tool state and operations
 */
export const useMapViewSettings = () => {
  const {
    useMapViewSettings: hook,
    handleMapViewSettingsOpen,
    handleMapViewSettingsClose,
    setToolCollapsed,
  } = useTools()
  const result = hook()

  return useMemo(
    () => ({
      isVisible: result.isVisible,
      mapViewGroup: result.mapViewGroup,
      collapsed: result.collapsed,
      open: handleMapViewSettingsOpen,
      close: handleMapViewSettingsClose,
      toggle: (mapViewGroup: MapViewGroup | null) => {
        if (!mapViewGroup) return
        if (result.isVisible && result.mapViewGroup?.id === mapViewGroup.id) {
          handleMapViewSettingsClose()
        } else {
          handleMapViewSettingsOpen(mapViewGroup)
        }
      },
      toggleCollapse: () => {
        setToolCollapsed('MapViewSettings', !result.collapsed)
      },
    }),
    [
      result.isVisible,
      result.mapViewGroup?.id,
      result.collapsed,
      handleMapViewSettingsOpen,
      handleMapViewSettingsClose,
      setToolCollapsed,
    ]
  )
}

/**
 * useNotes - Hook for Notes tool state and operations
 */
export const useNotes = () => {
  const {
    useNotes: hook,
    handleNotesShow,
    handleNotesHide,
    setToolCollapsed,
  } = useTools()
  const result = hook()

  return useMemo(
    () => ({
      isVisible: result.isVisible,
      collapsed: result.collapsed,
      mapViewNotes: result.mapViewNotes,
      show: handleNotesShow,
      hide: handleNotesHide,
      toggleCollapse: () => {
        setToolCollapsed('Notes', !result.collapsed)
      },
    }),
    [
      result.isVisible,
      result.collapsed,
      result.mapViewNotes,
      handleNotesShow,
      handleNotesHide,
      setToolCollapsed,
    ]
  )
}

/**
 * useDraw - Hook for Draw tool state and operations
 */
export const useDraw = () => {
  const { useDraw: hook, setToolVisible, handleDrawSetTargetLayer } = useTools()
  const result = hook()

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isVisible: result.isVisible,
      targetLayer: result.targetLayer,
      toggle: () => {
        setToolVisible('Draw', !result.isVisible)
      },
      setTargetLayer: handleDrawSetTargetLayer,
    }),
    [
      result.isVisible,
      result.targetLayer,
      setToolVisible,
      handleDrawSetTargetLayer,
    ]
  )
}
